import { useCallback, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Monitor, Pencil, Play, X } from "lucide-react";
import { QueryErrorCard } from "@/components/QueryErrorCard";
import { Guarded } from "@/components/Guarded";
import { StaleDataMarker, useLiveActionsDisabled } from "@/components/ConnectionBadge";
import { DeviceHealthPill } from "@/components/fleet/DeviceHealthPill";
import { RunStatusPill } from "@/components/fleet/RunStatusPill";
import { RunsTable } from "@/components/fleet/RunsTable";
import { EmptyState } from "@/components/fleet/EmptyState";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Pill } from "@/components/ui/Pill";
import { Skeleton } from "@/components/ui/Skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { Tooltip } from "@/components/ui/Tooltip";
import { useNow } from "@/components/ClockProvider";
import { useApiMutation, useApiQuery } from "@/hooks/useApi";
import { useTopic } from "@/hooks/useConsoleSocket";
import { ApiError, get, patch } from "@/lib/api";
import { qk, topicDevice } from "@/lib/queryKeys";
import { S, T } from "@/lib/strings";
import { absoluteTime, formatBytes, formatDuration, timeAgo } from "@/lib/utils";
import type { DeviceDetail as DeviceDetailModel, DeviceFrame } from "@/types/fleet";

const LOG_TAIL = 500;

export function DeviceDetail() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const now = useNow();

  const tab = params.get("tab") ?? "overview";
  const setTab = (t: string) => setParams({ tab: t }, { replace: true });

  const device = useApiQuery<DeviceDetailModel>(qk.device(id), `/devices/${id}`, { enabled: !!id });

  // Health, a11y and screen state arrive as device frames; they change the
  // header, so let the query be re-read rather than patching a dozen fields.
  useTopic(
    id ? topicDevice(id) : null,
    useCallback(
      (d: unknown) => {
        const frame = d as DeviceFrame;
        if (!frame?.kind) return;
        void qc.invalidateQueries({ queryKey: qk.device(id) });
      },
      [id, qc],
    ),
  );

  const live = useLiveActionsDisabled();

  if (device.isError) {
    return (
      <>
        <BackLink />
        <QueryErrorCard error={device.error} onRetry={() => void device.refetch()} />
      </>
    );
  }

  const d = device.data;

  return (
    <>
      <BackLink />

      {device.isPending || !d ? (
        <Skeleton className="mb-5 h-16 w-full" />
      ) : (
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <LabelEditor device={d} />
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="font-mono text-[11px] text-[var(--color-fg-faint)]">{d.id}</span>
              <DeviceHealthPill device={d} showLastSeen />
              {d.disarmed && (
                <Pill variant="warn" size="sm">
                  {S.deviceDisarmed}
                </Pill>
              )}
              {d.revoked && (
                <Pill variant="danger" size="sm">
                  {S.deviceRevoked}
                </Pill>
              )}
              {d.control.enabled && (
                <Pill variant="info" size="sm">
                  {S.deviceUnderControl}
                </Pill>
              )}
              <StaleDataMarker />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Guarded permission="dispatch_run">
              <Button
                variant="primary"
                onClick={() => navigate(`/dispatch?devices=${encodeURIComponent(d.id)}`)}
                disabled={live.disabled}
                title={live.reason}
              >
                <Play size={13} />
                {S.runNow}
              </Button>
            </Guarded>
            <Tooltip content={S.remoteControlPhase2}>
              <span>
                <Button variant="default" disabled title={S.remoteControlPhase2}>
                  <Monitor size={13} />
                  {S.remoteControl}
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">{S.deviceTabOverview}</TabsTrigger>
          <TabsTrigger value="runs">{S.deviceTabRuns}</TabsTrigger>
          <TabsTrigger value="settings">{S.deviceTabSettings}</TabsTrigger>
          <TabsTrigger value="logs">{S.deviceTabLogs}</TabsTrigger>
          <TabsTrigger value="control" disabled hint={S.remoteControlPhase2}>
            {S.deviceTabControl}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          {device.isPending || !d ? (
            <div className="grid gap-3 lg:grid-cols-2">
              <Skeleton className="h-64" />
              <Skeleton className="h-64" />
            </div>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2">
              <IdentityCard device={d} now={now} />
              <div className="space-y-3">
                <RunCard device={d} />
                <AssignedTasksCard device={d} now={now} />
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="runs">
          <RunsTable
            filter={{ device_id: id }}
            hideDeviceColumn
            emptyBody="這部裝置還沒有執行過任何 run。用上面的「立即執行」派一個任務給它。"
          />
        </TabsContent>

        <TabsContent value="settings">
          {device.isPending || !d ? <Skeleton className="h-64" /> : <SettingsTab device={d} />}
        </TabsContent>

        <TabsContent value="logs">
          <LogsTab deviceId={id} online={d?.online ?? false} />
        </TabsContent>

        <TabsContent value="control">
          <EmptyState icon={Monitor} title={S.phase2Title} body={S.phase2Control} />
        </TabsContent>
      </Tabs>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Header label — inline editable
 * ------------------------------------------------------------------ */

function LabelEditor({ device }: { device: DeviceDetailModel }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(device.label);

  const save = useApiMutation<DeviceDetailModel, string>(
    (label) => patch<DeviceDetailModel>(`/devices/${device.id}`, { label }),
    {
      invalidates: [qk.device(device.id), ["devices"]],
      successMessage: S.labelSaved,
      errorTitle: S.mutationFailed,
      onSuccess: () => setEditing(false),
    },
  );

  if (!editing) {
    return (
      <div className="group flex items-center gap-2">
        <h1 className="text-xl font-semibold tracking-[-0.24px]">{device.label}</h1>
        <Guarded permission="manage_device">
          <button
            type="button"
            onClick={() => {
              setValue(device.label);
              setEditing(true);
            }}
            aria-label={`${S.edit} ${S.fieldLabel}`}
            className="text-[var(--color-fg-faint)] opacity-0 transition-opacity hover:text-[var(--color-fg)] focus-visible:opacity-100 group-hover:opacity-100"
          >
            <Pencil size={13} />
          </button>
        </Guarded>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <Input
        value={value}
        autoFocus
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && value.trim()) save.mutate(value.trim());
          if (e.key === "Escape") setEditing(false);
        }}
        className="max-w-[280px] text-lg font-semibold"
        aria-label={S.fieldLabel}
      />
      <Button
        size="sm"
        variant="primary"
        disabled={save.isPending || !value.trim()}
        onClick={() => save.mutate(value.trim())}
        aria-label={S.save}
      >
        <Check size={13} />
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)} aria-label={S.cancel}>
        <X size={13} />
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Overview tab
 * ------------------------------------------------------------------ */

function IdentityCard({ device, now }: { device: DeviceDetailModel; now: number }) {
  const rows: Array<[string, React.ReactNode]> = [
    [S.fieldModel, device.model],
    [S.fieldManufacturer, device.manufacturer ?? "—"],
    [S.fieldAndroid, `SDK ${device.android_sdk}`],
    [S.fieldAppVersion, `${device.app_version_name} (${device.app_version_code})`],
    [S.fieldGroup, device.group_id ?? "—"],
    [
      S.fieldTags,
      device.tags.length ? (
        <div className="flex flex-wrap gap-1">
          {device.tags.map((t) => (
            <span
              key={t}
              className="rounded bg-[rgba(255,255,255,0.06)] px-1.5 py-0.5 text-[10px]"
            >
              {t}
            </span>
          ))}
        </div>
      ) : (
        "—"
      ),
    ],
    [S.colBattery, device.battery_pct === null ? "—" : `${device.battery_pct}%${device.charging ? " ⚡" : ""}`],
    [S.fieldNetwork, device.network_type ?? "—"],
    [S.fieldStorage, formatBytes(device.free_storage_bytes)],
    [S.fieldUptime, formatDuration(device.uptime_ms)],
    [S.fieldPendingOutbox, String(device.pending_outbox ?? 0)],
    [S.fieldConfigVersion, String(device.config_version)],
    [S.fieldTaskSetVersion, String(device.task_set_version)],
    [S.fieldEnrolledAt, absoluteTime(device.enrolled_at)],
    [S.fieldLastSeen, `${timeAgo(device.last_seen_at, now)} · ${absoluteTime(device.last_seen_at)}`],
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{S.identity}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="space-y-1.5">
          {rows.map(([k, v]) => (
            <div key={k} className="flex gap-3 text-[12px]">
              <dt className="w-32 shrink-0 text-[var(--color-fg-subtle)]">{k}</dt>
              <dd className="min-w-0 break-words font-mono text-[var(--color-fg-muted)]">{v}</dd>
            </div>
          ))}
        </dl>
        {device.notes && (
          <div className="mt-3 border-t border-[var(--color-border-subtle)] pt-2">
            <div className="text-[11px] uppercase tracking-wider text-[var(--color-fg-subtle)]">
              {S.fieldNotes}
            </div>
            <p className="mt-0.5 text-[12px] text-[var(--color-fg-muted)]">{device.notes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RunCard({ device }: { device: DeviceDetailModel }) {
  const active = device.active_run;
  const last = device.last_run;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{active ? S.currentRun : S.lastRun}</CardTitle>
      </CardHeader>
      <CardContent>
        {active ? (
          <Link
            to={`/runs/${active.id}`}
            className="flex items-center gap-2.5 rounded-md border border-[var(--color-border-subtle)] px-3 py-2 transition-colors hover:bg-[rgba(255,255,255,0.04)]"
          >
            <RunStatusPill status="RUNNING" size="sm" />
            <span className="truncate text-[13px] text-[var(--color-fg)]">{active.task_name}</span>
            <span className="ml-auto font-mono text-[11px] text-[#f59e0b]">
              {T.stepProgress(active.n, active.max_steps)}
            </span>
          </Link>
        ) : last ? (
          <Link
            to={`/runs/${last.id}`}
            className="flex items-center gap-2.5 rounded-md border border-[var(--color-border-subtle)] px-3 py-2 transition-colors hover:bg-[rgba(255,255,255,0.04)]"
          >
            <RunStatusPill status={last.status} size="sm" />
            <span className="truncate font-mono text-[11px] text-[var(--color-fg-muted)]">
              {last.id}
            </span>
            <span className="ml-auto text-[11px] text-[var(--color-fg-subtle)]">
              {absoluteTime(last.finished_at)}
            </span>
          </Link>
        ) : (
          <p className="text-xs text-[var(--color-fg-faint)]">{S.noCurrentRun}</p>
        )}
      </CardContent>
    </Card>
  );
}

function AssignedTasksCard({ device, now }: { device: DeviceDetailModel; now: number }) {
  const tasks = device.assigned_tasks ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>{S.assignedTasks}</CardTitle>
      </CardHeader>
      <CardContent>
        {tasks.length === 0 ? (
          <EmptyState title={S.assignedTasks} body={S.noAssignedTasks} compact />
        ) : (
          <ul className="space-y-1">
            {tasks.map((t) => (
              <li key={t.task_id}>
                <Link
                  to={`/tasks/${t.task_id}`}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-[rgba(255,255,255,0.04)]"
                >
                  <span className="truncate text-[13px] text-[var(--color-fg)]">{t.task_name}</span>
                  {!t.enabled && (
                    <Pill variant="muted" size="sm" dot={false}>
                      {S.disabled}
                    </Pill>
                  )}
                  <span className="ml-auto whitespace-nowrap text-[11px] text-[var(--color-fg-subtle)]">
                    {t.next_fire_at ? `${S.nextFire} ${timeAgo(t.next_fire_at, now)}` : S.scheduleNone}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Settings tab — two read-only columns
 * ------------------------------------------------------------------ */

function SettingsTab({ device }: { device: DeviceDetailModel }) {
  const settings = device.settings ?? {};
  const managed = new Set(device.managed ?? []);
  const fleetEntries = Object.entries(settings).filter(([k]) => managed.has(k));
  const localEntries = Object.entries(device.device_local ?? {});
  // Anything the server sent but does not claim is device-local by definition.
  const unmanaged = Object.entries(settings).filter(([k]) => !managed.has(k));

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>{S.settingsFleetManaged}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-2 text-[11px] text-[var(--color-fg-faint)]">{S.settingsFleetManagedHint}</p>
          <SettingsList entries={fleetEntries} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{S.settingsDeviceLocal}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-2 text-[11px] text-[var(--color-fg-faint)]">{S.settingsDeviceLocalHint}</p>
          <SettingsList entries={[...localEntries, ...unmanaged]} />
        </CardContent>
      </Card>
    </div>
  );
}

function SettingsList({ entries }: { entries: Array<[string, unknown]> }) {
  if (entries.length === 0) {
    return <p className="text-[11px] text-[var(--color-fg-faint)]">{S.empty}</p>;
  }
  return (
    <dl className="space-y-1">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-3 text-[12px]">
          <dt className="w-44 shrink-0 truncate font-mono text-[var(--color-fg-subtle)]">{k}</dt>
          <dd className="min-w-0 break-all font-mono text-[var(--color-fg-muted)]">
            {v === null || v === "" ? "—" : String(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/* ------------------------------------------------------------------ *
 * Logs tab — on demand only
 * ------------------------------------------------------------------ */

function LogsTab({ deviceId, online }: { deviceId: string; online: boolean }) {
  const [enabled, setEnabled] = useState(false);

  // Fetching logs issues a live `logs.tail` command to the handset, so it never
  // happens on tab mount and never polls — the operator asks for it explicitly.
  const logs = useQuery<{ lines: string[] }, ApiError>({
    queryKey: qk.deviceLogs(deviceId),
    queryFn: () => get<{ lines: string[] }>(`/devices/${deviceId}/logs?tail=${LOG_TAIL}`),
    enabled,
    refetchInterval: false,
    staleTime: 30_000,
    retry: false,
  });

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="default"
          onClick={() => {
            if (enabled) void logs.refetch();
            else setEnabled(true);
          }}
          disabled={logs.isFetching || !online}
          title={online ? undefined : S.logsOfflineError}
        >
          {logs.isFetching ? S.logsFetching : S.logsFetch}
        </Button>
        <span className="text-[11px] text-[var(--color-fg-faint)]">
          {S.logsHint} · {T.logsTail(LOG_TAIL)}
        </span>
      </div>

      {!online && (
        <EmptyState
          title={S.logsOfflineError}
          body="裝置上線後就可以抓取日誌。離線期間伺服器沒有可回放的紀錄。"
          compact
        />
      )}

      {logs.isError && <QueryErrorCard error={logs.error} onRetry={() => void logs.refetch()} />}

      {logs.data && (
        <pre className="max-h-[60vh] overflow-auto rounded-lg border border-[var(--color-border-subtle)] bg-[rgba(0,0,0,0.3)] p-3 font-mono text-[11px] leading-relaxed text-[var(--color-fg-muted)]">
          {logs.data.lines.join("\n")}
        </pre>
      )}

      {enabled && !logs.data && !logs.isError && logs.isFetching && (
        <Skeleton className="h-64 w-full" />
      )}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/devices"
      className="mb-3 inline-flex items-center gap-1 text-[12px] text-[var(--color-fg-subtle)] hover:text-[var(--color-fg)]"
    >
      <ArrowLeft size={12} />
      {S.devicesTitle}
    </Link>
  );
}
